import { useState, useEffect, useRef, useContext } from "react";
// import { refreshEllipseOptions } from "../utils/ellipseUtils";
import { useAppContext } from "../context/appContext";
import { ShotDataContext } from "../context/shotDataContext";
import ContextTypes from "../context/contextTypes";


export function useEllipseData({ contextType, sessionEllipseData, selectedClubs, shots, sessionId, startDate, endDate, qualityFilterState, includeTags, excludeTags }) {
    const { allShotEllipseData, setAllShotEllipseData } = useContext(ShotDataContext);
    const [isLoading, setIsLoading] = useState(false);
    const [fetchCount, setFetchCount] = useState(0);

    const [ellipseOptions, setEllipseOptions] = useState({});
    const [filteredEllipseOptions, setFilteredEllipseOptions] = useState({});
    const [ellipsesToUse, setEllipsesToUse] = useState(ellipseOptions);
    const clubsBeingFetched = useRef(new Set());

    const { fetchEllipseDataForShotsAndSession } = useAppContext();



    useEffect(() => {
        if (clubsBeingFetched.current.size > 0) {
            setIsLoading(true);
        } else {
            //add a time delay of 2 seconds
            setIsLoading(false);
        }
    }, [fetchCount]);


    useEffect(() => {
        if (contextType === ContextTypes.ALL_SHOTS) { // Logic for handling all shots scenario

            //First extract the default and cleaned ellipse data 
            const allShotsEllipseOptions = extractEllipseOptions(allShotEllipseData, 'ellipseParameters');
            const filteredShotsEllipseOptions = extractEllipseOptions(allShotEllipseData, 'filteredEllipseParameters');
            setEllipseOptions(allShotsEllipseOptions);
            setFilteredEllipseOptions(filteredShotsEllipseOptions);

            if (!selectedClubs) { return }

            // then check for and fetch any missing ellipses
            if (
                (!shots || !ellipseOptions ||
                    Object.keys(selectedClubs).length === 0 ||
                    Object.keys(ellipseOptions).length === Object.keys(selectedClubs).length) && !startDate && !endDate && !includeTags && !excludeTags
            ) {
                return;
            } else { //some ellipse data is missing
                fetchMissingEllipseData();
            }


        } else if (contextType === ContextTypes.SESSION) {
            // Logic for handling session-specific data

            if (shots?.length > 0 && (Object.keys(ellipseOptions).length !== Object.keys(selectedClubs).length) && !startDate && !endDate && sessionId) {
                fetchMissingEllipseData();
            }
        }
    }, [contextType, allShotEllipseData, selectedClubs, shots, sessionId, startDate, endDate, includeTags, excludeTags]);


    const formatDateForComparison = (dateStr) => {
        return new Date(dateStr).setHours(0, 0, 0, 0);
    };


    //only needed for allShotsEllipseData as it stores default and quality-cleaned versions of ellipses
    const extractEllipseOptions = (ellipseData, optionType) => {


        //TODO: nearly working but if I have an ellipse with a date match and a tag match, it seems to still get a bit confused


        const doTagsMatch = (filterTagIds, ellipseTagIds) => {
            // If there are no filter tags and ellipse also has no tags, it's a match.
            if (!filterTagIds.length && !ellipseTagIds.length) return true;

            // If there are no filter tags but ellipse has tags, it's not a match.
            if (!filterTagIds.length) return false;

            // If there are filter tags but ellipse has no tags, it's not a match.
            if (!ellipseTagIds.length) return false;

            // Create sets for easy comparison.
            const filterSet = new Set(filterTagIds);
            const ellipseSet = new Set(ellipseTagIds);

            // Every tag in the filter set must be present in the ellipse set.
            return [...filterSet].every(tag => ellipseSet.has(tag));
        };


        const filteredEllipses = ellipseData?.filter(ellipse => {

            // Convert dates to standard format for comparison
            const ellipseStartDate = ellipse.startDate ? formatDateForComparison(ellipse.startDate) : null;
            const ellipseEndDate = ellipse.endDate ? formatDateForComparison(ellipse.endDate) : null;
            const inputStartDate = startDate ? formatDateForComparison(startDate) : null;
            const inputEndDate = endDate ? formatDateForComparison(endDate) : null;


            if (!ellipse) return false;

            // Handle no dates and no tags condition
            if (!startDate && !endDate && !includeTags?.length && !excludeTags?.length) {
                return !ellipse?.startDate && !ellipse?.endDate &&
                    !ellipse.includeTags?.length && !ellipse.excludeTags?.length;
            }

            // Check for exact matches on startDate and endDate
            if (startDate && ellipseStartDate !== inputStartDate) {
                return false;
            }

            if (endDate && ellipseEndDate !== inputEndDate) {
                return false;
            }

            // Check for exact matches on includeTags and excludeTags
            if (includeTags?.length && !doTagsMatch(includeTags.map(tagObj => tagObj.id), ellipse?.includeTags)) {
                return false;
            }

            if (excludeTags?.length && doTagsMatch(excludeTags.map(tagObj => tagObj.id), ellipse?.excludeTags)) {
                return false;
            }

            return true;
        });

        // Now, let's extract the first matching ellipse for each club
        const clubFilteredOptions = {};

        filteredEllipses.forEach(ellipse => {
            if (!clubFilteredOptions[ellipse.club]) {
                clubFilteredOptions[ellipse.club] = ellipse[optionType];
            }
        });

        return clubFilteredOptions;
    };

    const formatDate = (date) => {
        const dateObj = new Date(date);
        return `${dateObj.getFullYear()}-${dateObj.getMonth() + 1}-${dateObj.getDate()}`;
    };

    const fetchEllipseDataForClub = async (club) => {

        const doTagsMatch = (filterTagIds, dataTagIds) => {
            const filterSet = new Set(filterTagIds);
            const dataSet = new Set(dataTagIds);

            const isSubset = [...filterSet].every(tag => dataSet.has(tag));

            return isSubset && filterSet.size === dataSet.size;
        };

        const hasMatchingData = (data) => {

            if (data?.club !== club) return false;

            const dataStartDateStr = data.startDate ? formatDate(data.startDate) : null;
            const dataEndDateStr = data.endDate ? formatDate(data.endDate) : null;

            if (startDate && dataStartDateStr !== formatDate(startDate)) {
                return false;
            } else {
            }

            if (endDate && dataEndDateStr !== formatDate(endDate)) {
                return false;
            } else {

            }


            if (includeTags?.length && !doTagsMatch(includeTags?.map(tagObj => tagObj.id), data.includeTags)) {
                return false;
            }

            if (excludeTags?.length && !doTagsMatch(excludeTags?.map(tagObj => tagObj.id), data.excludeTags)) {
                return false;
            }

            return true;
        };

        if (!sessionId) {
            const existingData = allShotEllipseData?.find(hasMatchingData);

            if (existingData) {
                return;
            }
        } else {
            const existingData = sessionEllipseData?.find(hasMatchingData);

            if (existingData) {
                return;
            }
        }

        if (!clubsBeingFetched.current.has(club)) {
            let fetchedEllipseData;

            let shotsToFindEllipseFor = shots.filter((shot) => shot.Club === club);
            if (shotsToFindEllipseFor.length < 3) {
                return;
            }
            clubsBeingFetched.current.add(club);
            setFetchCount(prevCount => prevCount + 1);  // Increment fetchCount

            fetchedEllipseData = await fetchEllipseDataForShotsAndSession({
                club,
                startDate,
                endDate,
                sessionId,
                includeTags: includeTags?.map(tag => tag.id) || null,  // passing only ids
                excludeTags: excludeTags?.map(tag => tag.id) || null   // passing only ids
            });
            clubsBeingFetched.current.delete(club);
            setFetchCount(prevCount => prevCount - 1);  // Decrement fetchCount

            if (fetchedEllipseData) {
                let ellipseObject = fetchedEllipseData[0].result.ellipseParameters;

                if (contextType === ContextTypes.ALL_SHOTS) {
                    setAllShotEllipseData(prevData => [...prevData, ellipseObject]);
                } else if (contextType === ContextTypes.SESSION) {
                    let cleanedResult = { "club": fetchedEllipseData[0].result.club, "session": fetchedEllipseData[0].result.ellipseParameters.session, "ellipseParameters": fetchedEllipseData[0].result.ellipseParameters.ellipseParameters }
                    sessionEllipseData.push(cleanedResult)
                    const newEllipseOptions = sessionEllipseData?.reduce((acc, ellipse) => {
                        acc[ellipse.club] = ellipse.ellipseParameters;
                        return acc;
                    }, {});
                    setEllipseOptions(newEllipseOptions);
                }
            } else {
            }
        }
    };

    const fetchMissingEllipseData = async () => {
        if (!selectedClubs || Object.keys(selectedClubs).length === 0) {
            return
        }
        const fetchPromises = Object.keys(selectedClubs)
            .filter(club => selectedClubs[club])  // only consider the clubs that are selected
            .map(fetchEllipseDataForClub);
        await Promise.all(fetchPromises);
    };

    useEffect(() => {
        if (qualityFilterState) {
            setEllipsesToUse(() => filteredEllipseOptions);
        } else {
            setEllipsesToUse(() => ellipseOptions);
        }
    }, [qualityFilterState, ellipseOptions, filteredEllipseOptions]);



    return {
        ellipseOptions, setEllipseOptions, filteredEllipseOptions, setFilteredEllipseOptions, ellipsesToUse,
        fetchMissingEllipseData, extractEllipseOptions, isLoading
    };
} 